import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Info, Save } from "@mui/icons-material";
import { Card, CardActions, CardContent, CardHeader, Button, Grid, FormLabel, Paper, TextField, Alert, AlertTitle, Autocomplete, Typography, Skeleton } from '@mui/material';
import { useEnrollmentInfo } from "../../providers/EnrollmentProvider";
import LoadingButton from "../../components/LoadingButton";

//import ConfirmActionButton from "../../components/ConfirmActionButton";
import { YearInput } from "../../components/FormattedInputs";
import { isValidYear } from "../../util/Validators";
import EnrollmentService from "../../services/EnrollmentService";
import axios from "axios";

function AdditionalInformation({ navigateBack, navigateForward, save, saveAndNavigate }) {
    const { localUnionInfo: { localUnion }, additionalInfo, localUnionInfo } = useEnrollmentInfo();
    const { control, formState: { isValid, isSubmitting, isValidating }, handleSubmit, reset, trigger } = useForm({
        mode: "onChange",
        reValidateMode: "onChange"
    });

    const [isLoading, setIsLoading] = useState(false);
    const [additionalOptions, setAdditionalOptions] = useState();
    const submitForm = handleSubmit(saveAndNavigate);

    const next = () => {
        trigger().then((isValid) => {
            if (isValid) {
                submitForm().then(() => {
                    navigateForward();
                });
            }
        });
    };

    const loadAdditionalOptions = (cancelToken) => {
        setIsLoading(true);

        return EnrollmentService.loadAdditionalNMEOptionsByLocalUnionId(localUnion.id, cancelToken)
            .then(setAdditionalOptions)
            .then(() => setIsLoading(false))
    }

    useEffect(() => {
        const source = axios.CancelToken.source();

        if (additionalInfo && (additionalInfo.recruiterName || additionalInfo.yearEnteredProfession || additionalInfo.comments)) {
            reset({ additionalInfo })
        }
        
        return function cleanup() {
            // Cancels requests (cleanup) on component unmount
            source.cancel()
        }

    }, [reset, additionalInfo])

    useEffect(() => {
        const source = axios.CancelToken.source();

        loadAdditionalOptions(source.token);

        return function cleanup() {
            // Cancels requests (cleanup) on component unmount
            source.cancel()
        }
        
    }, [])

    return (
        <Card variant="outlined">
            <CardHeader title="Additional Information" />
            <CardContent>
                <form>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Card variant="outlined" className={isLoading || !additionalOptions || additionalOptions.length === 0 ? "disabled" : null}>
                                <CardHeader subheader="Options for this Local Union" />
                                {isLoading ? (
                                    <CardContent style={{ paddingTop: "0", paddingBottom: "8px" }}>
                                        <Skeleton width="25%" height={20} />
                                        <Skeleton width="50%" height={50} />
                                    </CardContent>
                                ) : additionalOptions && additionalOptions.length > 0 ? (
                                    <CardContent style={{ paddingTop: "0" }}>
                                        <Grid container spacing={2}>
                                            {additionalOptions.map(({ listId, label, dataLabel, options }) => (
                                                <Grid key={listId} item xs={12}>
                                                    <Controller
                                                        name={`additionalInfo.options.${dataLabel}.listId`}
                                                        control={control}
                                                        defaultValue={listId}
                                                        render={({ field }) => (
                                                            <input type="hidden" {...field} />
                                                        )}
                                                    />
                                                    <Controller
                                                        name={`additionalInfo.options.${dataLabel}.selectedOption`}
                                                        control={control}
                                                        defaultValue={null}
                                                        render={({ field, fieldState: { error } }) => (
                                                            <Autocomplete
                                                                {...field}
                                                                autoSelect openOnFocus
                                                                options={options}
                                                                getOptionLabel={option => option?.name}
                                                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        error={Boolean(error)}
                                                                        label={label}
                                                                        variant="outlined"
                                                                        fullWidth />
                                                                )}
                                                                onChange={(_, data) => field.onChange(data)}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </CardContent>
                                ) : (
                                    <CardContent style={{ paddingTop: "0", paddingBottom: "8px", display: "flex", gap: "0.5rem", alignItems: "center" }}>
                                        <Info />
                                        <Typography variant="subtitle2">
                                            No additional options available.
                                        </Typography>
                                    </CardContent>
                                )}
                            </Card>
                        </Grid>
                        <Grid item xs={12}>
                            <Paper variant="outlined" style={{ padding: "1rem" }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormLabel component="legend">Miscellaneous</FormLabel>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Controller
                                            name="additionalInfo.recruiterName"
                                            control={control}
                                            defaultValue={""}
                                            render={({ field, fieldState: { error } }) => (
                                                <TextField
                                                    label="Name of Recruiter"
                                                    variant="outlined"
                                                    error={Boolean(error)}
                                                    fullWidth {...field} />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Controller
                                            name="additionalInfo.yearEnteredProfession"
                                            control={control}
                                            defaultValue={""}
                                            rules={{
                                                validate: {
                                                    validateYear: (maskedYear) => {
                                                        // Clears the placeholder mask (____) 
                                                        const yearNumber = maskedYear.replaceAll("_", "")
                                                        // If there are any numbers, validate the string, if not, return true (empty is considered valid)
                                                        return yearNumber && yearNumber.length > 0 ? isValidYear(yearNumber) : true
                                                    }
                                                }
                                            }}
                                            render={({ field, fieldState: { error } }) => (
                                                <YearInput {...field}
                                                    inputProps={{
                                                        label: "Year entered the profession",
                                                        helperText: (error && error.message) || "Ex: 2020 (numbers only)",
                                                        variant: "outlined",
                                                        error: Boolean(error),
                                                        fullWidth: true
                                                    }}>
                                                </YearInput>
                                            )}
                                        />
                                    </Grid>
                                    {/* Show Anticipated Graduation Date only for Student or College Student enrollee types */}
                                    {localUnionInfo?.job && (localUnionInfo.job.name?.toLowerCase().includes('student') || localUnionInfo.job.name?.toLowerCase().includes('college student')) && (
                                        <Grid item xs={12}>
                                            <Controller
                                                name="additionalInfo.anticipatedGraduationDate"
                                                control={control}
                                                defaultValue={""}
                                                rules={{ required: true}}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        label="Anticipated Graduation Date"
                                                        variant="outlined"
                                                        helperText={"Examples: 5/25/2028, May 2028, 2028, Spring 2028"}
                                                        error={Boolean(error)}
                                                        required
                                                        fullWidth {...field} />
                                                )}
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <Controller
                                            name="additionalInfo.comments"
                                            control={control}
                                            defaultValue={""}
                                            render={({ field }) => (
                                                <TextField
                                                    minRows={4}
                                                    label="Comments"
                                                    variant="outlined"
                                                    helperText="Please share any comments about why you joined your union."
                                                    multiline fullWidth {...field} />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    </Grid>
                </form>
            </CardContent>
            <CardActions className="space-between">
                <Grid item container xs={12} alignItems="center">
                    <Grid item md={7} xs={3} lg={8}>
                        <Button onClick={navigateBack}>Back</Button>
                    </Grid>
                    <Grid item md={3} xs={2} lg={3}>
                        <Typography size="small" component="div" variant="text" align="right"></Typography>
                        &nbsp;
                    </Grid>
                    <Grid item md={2} xs={7} lg={1} align="right">
                        <CardActions className="space-between">
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                onClick={next}
                                disabled={ isLoading }
                                loading={isSubmitting || isValidating}
                            >
                                Next
                            </LoadingButton>
                        </CardActions>
                        {/* <CardActions className="space-between">
                            <ConfirmActionButton
                                cancelText="Cancel"
                                confirmText="Submit"
                                confirmIcon={<Save />}
                                dialogTitle="Confirm enrollment submission"
                                dialogContent={
                                    <Alert severity="warning" variant="outlined">
                                        <AlertTitle><b>Please read carefully</b></AlertTitle>
                                        By clicking the "Submit" button below you agree that you provided truthful information to the best of your ability and that in order to change this information you will need to contact your local union.
                                    </Alert>
                                }
                                onDialogWillOpen={handleClickSubmitAndSign}
                                onConfirm={navigateForward}
                                disabled={isLoading}
                                loading={isSubmitting || isValidating}
                            >
                                Submit &amp; Sign
                            </ConfirmActionButton>
                        </CardActions> */}
                    </Grid>
                </Grid>
            </CardActions>
        </Card>
    )
}

export default AdditionalInformation;